import { apiFetch } from "./client";

/**
 * Nalazi spoljnih laboratorija: koja laboratorija, šta je izmerila, i sam izveštaj.
 *
 * Vrednosti stoje uz fajl, ne umesto njega. Arhiv je pokazao šta košta kad ih nema: mikrobiologija je
 * 2025. naručena 1.396 puta i izmerena nula puta u njihovom sistemu, jer se radi spolja a nalaz sleti u
 * fajl koji ništa ne može da pročita.
 */

export const EXTERNAL_PURPOSES = ["INTERNAL", "FOR_CUSTOMER", "DISPUTE"] as const;
export type ExternalPurpose = (typeof EXTERNAL_PURPOSES)[number];

export type ExternalLaboratory = {
  id: string;
  name: string;
  accreditation: string | null;
  note: string | null;
  sortOrder: number;
  active: boolean;
  /** Koliko nalaza visi na njoj — po tome se zna koja se stavka još sme preimenovati. */
  reports: number;
};

export type ExternalValue = {
  parameterId: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  decimals: number;
  resultType: string | null;
  valueNum: number | null;
  valueBool: boolean | null;
  valueText: string | null;
  note: string | null;
};

export type ExternalAnalysis = {
  id: string;
  laboratoryId: string | null;
  laboratoryName: string | null;
  subject: string;
  reportNumber: string | null;
  issuedOn: string | null;
  purpose: ExternalPurpose;
  deliveryId: string | null;
  receivingLotId: string | null;
  goodsLotId: string | null;
  productId: string | null;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  hasFile: boolean;
  note: string | null;
  recordedAt: string;
  verifiedAt: string | null;
  values: ExternalValue[];
};

export type ExternalValueInput = {
  parameterId: string;
  valueNum?: number | null;
  valueBool?: boolean | null;
  valueText?: string | null;
  note?: string | null;
};

export type CreateExternalAnalysisBody = {
  laboratoryId: string;
  subject: string;
  reportNumber?: string | null;
  issuedOn?: string | null;
  purpose?: ExternalPurpose;
  companyId?: string | null;
  deliveryId?: string | null;
  receivingLotId?: string | null;
  goodsLotId?: string | null;
  productId?: string | null;
  note?: string | null;
  values?: ExternalValueInput[];
};

/** Lista `values` izostavljena ostavlja vrednosti na miru; prazna ih briše. To su dve namere. */
export type UpdateExternalAnalysisBody = {
  laboratoryId?: string | null;
  subject?: string | null;
  reportNumber?: string | null;
  issuedOn?: string | null;
  purpose?: ExternalPurpose;
  note?: string | null;
  values?: ExternalValueInput[];
};

function query(f: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const externalLabApi = {
  list: (activeOnly = true) =>
    apiFetch<ExternalLaboratory[]>(`/quality/external-laboratories?activeOnly=${activeOnly}`),
  create: (body: { name: string; accreditation?: string | null; note?: string | null; sortOrder?: number }) =>
    apiFetch<ExternalLaboratory>("/quality/external-laboratories", { method: "POST", body }),
  update: (
    id: string,
    body: {
      name?: string;
      accreditation?: string | null;
      note?: string | null;
      sortOrder?: number;
      active?: boolean;
    },
  ) => apiFetch<ExternalLaboratory>(`/quality/external-laboratories/${id}`, { method: "PUT", body }),
  retire: (id: string) => apiFetch<void>(`/quality/external-laboratories/${id}`, { method: "DELETE" }),
};

export const externalAnalysisApi = {
  search: (f: { laboratoryId?: string; purpose?: string; from?: string; to?: string; q?: string }) =>
    apiFetch<ExternalAnalysis[]>(`/quality/external-analyses${query(f)}`),
  forGoodsLot: (goodsLotId: string) =>
    apiFetch<ExternalAnalysis[]>(`/quality/external-analyses/lots/${goodsLotId}`),
  forDelivery: (deliveryId: string) =>
    apiFetch<ExternalAnalysis[]>(`/quality/external-analyses/deliveries/${deliveryId}`),
  create: (body: CreateExternalAnalysisBody) =>
    apiFetch<ExternalAnalysis>("/quality/external-analyses", { method: "POST", body }),
  update: (id: string, body: UpdateExternalAnalysisBody) =>
    apiFetch<ExternalAnalysis>(`/quality/external-analyses/${id}`, { method: "PUT", body }),
  remove: (id: string) => apiFetch<void>(`/quality/external-analyses/${id}`, { method: "DELETE" }),

  /**
   * Fajl ide u tri koraka: potpis, pretraživač ga šalje pravo u skladište, pa potvrda.
   *
   * Potpis ništa ne vezuje — veličina i tip se na potvrdi proveravaju ponovo, nad onim što je stvarno
   * stiglo u skladište, a ne nad onim što je pozivalac rekao da šalje.
   */
  presign: (id: string, body: { fileName: string; contentType: string; sizeBytes: number }) =>
    apiFetch<{ objectKey: string; url: string }>(`/quality/external-analyses/${id}/file/presign`, {
      method: "POST",
      body,
    }),
  confirm: (
    id: string,
    body: { objectKey: string; fileName: string; contentType: string; sizeBytes: number },
  ) =>
    apiFetch<ExternalAnalysis>(`/quality/external-analyses/${id}/file/confirm`, { method: "POST", body }),
  downloadUrl: (id: string) =>
    apiFetch<{ url: string }>(`/quality/external-analyses/${id}/file`),
};
